import React, { useState, useEffect, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import IconButton from '@material-ui/core/IconButton';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Button from '@material-ui/core/Button';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';

import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getCalendarFirstDay, createMonthlyCalendarData, getFirstDayOf6thWeek } from '../../common/calendarHelper';
import { CALENDAR_TYPE_MONTH, CALENDAR_TYPE_WEEK, INTERVIEW_STATUS_CONFIRMED } from '../../common/constants';
import Week from './Week';
import { loadCurrentMonthInterviewsByUserId } from '../../common/services/myCalendarServices';

import cx from 'clsx';
import { startOfMonth, subMonths, addMonths, getMonth, format, startOfDay, isEqual as isEqualDate, isWithinInterval } from 'date-fns';
import _filter from 'lodash/filter';
import _get from 'lodash/get';
// import _set from 'lodash/set';

const styles = theme => ({
    root: {
        //paddingRight: '64px',
        backgroundColor: '#FFFFFF',
        height: '100%',
    },
    monthlyCalendar: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height: '100%',
    },
    header: {
        background: '#FFFFFF',
        flex: '0 0 84px',
        display: 'flex',
    },
    headerLeft: {
        flexGrow: 3,
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { padding: '24px 0px 24px 10px' },
    },
    headerRight: {
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'flex-end',
    },
    keyboardArrowLeftIcon: {
        fontSize: '32px',
        backgroundColor: 'rgba(0, 0, 0, 0.03)',
        padding: '3px',
        borderRadius: '50%',
    },
    keyboardArrowRightIcon: {
        fontSize: '32px',
        backgroundColor: 'rgba(0, 0, 0, 0.03)',
        padding: '3px',
        borderRadius: '50%',
    },
    keyboardArrowLeftIconButton: {
        padding: 0,
        color: '#111E14',
    },
    keyboardArrowRightIconButton: {
        padding: 0,
        color: '#111E14',
        marginLeft: '8px',
    },
    month: {
        fontSize: '18px',
        color: '#111E14',
        fontWeight: 600,
        marginLeft: '16px',
        lineHeight: '32px',
        [theme.breakpoints.down('sm')]: { marginLeft: 6 },
    },
    year: {
        fontSize: '18px',
        color: '#9B9E9C',
        marginLeft: '5px',
        lineHeight: '32px',
    },
    monthWeekButtonGroupContainer: {
        padding: '24px 24px 24px 0px',
        [theme.breakpoints.down('sm')]: { padding: '24px 16px 24px 0px' },
    },
    monthWeekButtonGroup: {
        '& :not(:last-child)': {
            borderRight: 'none',
        },
    },
    monthWeekButtons: {
        background: '#1D8A8B',
        color: '#FFF',
        '&:hover': {
            backgroundColor: '#E0E1E5',
            color: '#000',
        },
    },
    monthWeekButtonOff: {
        background: 'rgba(0, 0, 0, 0.03)',
        color: '#111E14',
    },
    dayHeader: {
        flex: '0 0 33px',
        display: 'flex',
        // flex: '1 1 auto',
        width: '100%',
        maxHeight: '33px',
        color: '#9B9E9C',
        fontSize: '12px',
        borderTop: '1px solid #F0F0F0',
        borderBottom: '1px solid #F0F0F0',
        '& div': {
            // minWidth: '100px',
            flexGrow: 1,
            lineHeight: '33px',
            [theme.breakpoints.up('lg')]: { minWidth: '100px' },
            [theme.breakpoints.only('md')]: { minWidth: '70px' },
        },
    },
    weeksContainer: {
        flex: 1,
        overflow: 'hidden',
    },
});

const getDefns = params => {
    const { props, firstDayOfMonth, setFirstDayOfMonth, calendarData, calendarType, setCalendarType, interviewList } = params;
    const {
        classes,
        classes: { monthWeekButtons: monthWeekButtonsCls, monthWeekButtonOff: monthWeekButtonOffCls },
    } = props;

    let monthButtonClassNames = [monthWeekButtonsCls];
    if (calendarType !== CALENDAR_TYPE_MONTH) monthButtonClassNames.push(monthWeekButtonOffCls);
    let weekButtonClassNames = [monthWeekButtonsCls];
    if (calendarType !== CALENDAR_TYPE_WEEK) weekButtonClassNames.push(monthWeekButtonOffCls);

    const firstDayOf6thWeek = getFirstDayOf6thWeek(calendarData);

    const w0InterviewList = _filter(interviewList, item => _get(item, 'dayId', '').indexOf('w0') >= 0);
    const w1InterviewList = _filter(interviewList, item => _get(item, 'dayId', '').indexOf('w1') >= 0);
    const w2InterviewList = _filter(interviewList, item => _get(item, 'dayId', '').indexOf('w2') >= 0);
    const w3InterviewList = _filter(interviewList, item => _get(item, 'dayId', '').indexOf('w3') >= 0);
    const w4InterviewList = _filter(interviewList, item => _get(item, 'dayId', '').indexOf('w4') >= 0);
    const w5InterviewList = _filter(interviewList, item => _get(item, 'dayId', '').indexOf('w5') >= 0);

    let defns = {
        keyboardArrowLeftIconButton: {
            onClick: () => {
                setFirstDayOfMonth(subMonths(firstDayOfMonth, 1));
            },
        },
        keyboardArrowRightIconButton: {
            onClick: () => {
                setFirstDayOfMonth(addMonths(firstDayOfMonth, 1));
            },
        },
        monthWeekButtonGroup: { disableElevation: true },
        monthButton: {
            className: cx(monthButtonClassNames),
            onClick: setCalendarType.bind(null, CALENDAR_TYPE_MONTH),
        },
        weekButton: {
            className: cx(weekButtonClassNames),
            onClick: setCalendarType.bind(null, CALENDAR_TYPE_WEEK),
        },
        week0: {
            id: 'w0',
            interviewList: w0InterviewList,
        },
        week1: {
            id: 'w1',
            interviewList: w1InterviewList,
        },
        week2: {
            id: 'w2',
            interviewList: w2InterviewList,
        },
        week3: {
            id: 'w3',
            interviewList: w3InterviewList,
        },
        week4: {
            id: 'w4',
            interviewList: w4InterviewList,
        },
        week5: {
            id: 'w5',
            interviewList: w5InterviewList,
            isHidden: getMonth(firstDayOf6thWeek) !== getMonth(firstDayOfMonth),
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

const MonthlyCalendar = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    const context = useContext(LayoutContext);
    const { userId, calendarType, setCalendarType, selectedDate } = context;

    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    const [firstDayOfMonth, setFirstDayOfMonth] = useState(startOfMonth(new Date()));
    const [calendarData, setCalendarData] = useState(createMonthlyCalendarData(getCalendarFirstDay(firstDayOfMonth)));
    const [interviewList, setInterviewList] = useState([]);
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    const setConfirmedInterviewsCount = _get(props, 'setConfirmedInterviewsCount');
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    const currentMonthLabel = format(firstDayOfMonth, 'MMMM');
    const currentYearLabel = format(firstDayOfMonth, 'yyyy');
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    // load monthly calendar data
    useEffect(() => {
        // derive the calendar data for the new month
        const newCalendarData = createMonthlyCalendarData(getCalendarFirstDay(firstDayOfMonth));
        setCalendarData(newCalendarData);

        // load the interviews for the new month
        const unsubscribeOnSnapshot = loadCurrentMonthInterviewsByUserId(userId, newCalendarData, setInterviewList, setConfirmedInterviewsCount);

        // unsubscribe realtime update on unmount
        return () => {
            unsubscribeRealtimeUpdates([unsubscribeOnSnapshot]);
        };
    }, [userId, firstDayOfMonth, setConfirmedInterviewsCount]);

    // update Confirmed Interviews count
    useEffect(() => {
        // update confirmed interviews count only if selectedDate is in the current calendar (in case user navigated to a different month)
        const calendarFirstDate = _get(calendarData, 'w0d0.dt');
        const lastDayId = _get(calendarData, 'lastDayId');
        const calendarLastDate = _get(calendarData, `${lastDayId}.dt`);

        if (isWithinInterval(selectedDate, { start: calendarFirstDate, end: calendarLastDate })) {
            const interviewsOnSelectedDate = _filter(interviewList, data => {
                const startDt = _get(data, 'startDt');
                const startDtStartOfDay = startDt ? startOfDay(startDt.toDate()) : null;
                const isSelectedDateEqualToInterviewDate = isEqualDate(startDtStartOfDay, selectedDate);

                return isSelectedDateEqualToInterviewDate && _get(data, 'status') === INTERVIEW_STATUS_CONFIRMED;
            });

            setConfirmedInterviewsCount(interviewsOnSelectedDate.length);
        }
    }, [userId, selectedDate, calendarData, interviewList, setConfirmedInterviewsCount]);
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    const defns = getDefns({
        props,
        firstDayOfMonth,
        calendarData,
        calendarType,
        setFirstDayOfMonth,
        setCalendarType,
        interviewList,
    });
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Render
    return (
        <LayoutContext.Provider value={{ ...context, calendarData }}>
            <div {...defns.root}>
                <div {...defns.monthlyCalendar}>
                    <div {...defns.header}>
                        <div {...defns.headerLeft}>
                            <div>
                                <IconButton {...defns.keyboardArrowLeftIconButton}>
                                    <KeyboardArrowLeftIcon {...defns.keyboardArrowLeftIcon} />
                                </IconButton>
                                <IconButton {...defns.keyboardArrowRightIconButton}>
                                    <KeyboardArrowRightIcon {...defns.keyboardArrowRightIcon} />
                                </IconButton>
                            </div>

                            <div {...defns.month}>{currentMonthLabel}</div>
                            <div {...defns.year}>{currentYearLabel}</div>
                        </div>
                        <div {...defns.headerRight}>
                            <div {...defns.monthWeekButtonGroupContainer}>
                                <ButtonGroup variant="contained" aria-label="contained button group" {...defns.monthWeekButtonGroup}>
                                    <Button {...defns.monthButton}>Month</Button>
                                    <Button {...defns.weekButton}>Week</Button>
                                </ButtonGroup>
                            </div>
                        </div>
                    </div>
                    <div {...defns.dayHeader}>
                        <div>SUN</div>
                        <div>MON</div>
                        <div>TUE</div>
                        <div>WED</div>
                        <div>THU</div>
                        <div>FRI</div>
                        <div>SAT</div>
                    </div>
                    <div {...defns.weeksContainer}>
                        <SimpleBar style={{ height: '100%' }}>
                            <Week {...defns.week0} />
                            <Week {...defns.week1} />
                            <Week {...defns.week2} />
                            <Week {...defns.week3} />
                            <Week {...defns.week4} />
                            <Week {...defns.week5} />
                        </SimpleBar>
                    </div>
                </div>
            </div>
        </LayoutContext.Provider>
        // <div>test</div>
    );
    //---------------------------------------------------------------------
};

export default withStyles(styles)(MonthlyCalendar);
